/**
 * MilestoneCelebrationToast — B-2026-0075
 *
 * One-time overlay shown on EvaluationResults when the result payload contains
 * ``newly_awarded_milestones[]``.  Does NOT appear on page reloads (localStorage
 * guard keyed to evaluation_id).
 *
 * Usage:
 *   <MilestoneCelebrationToast
 *     evaluationId={evaluation.evaluation_id}
 *     milestoneIds={evaluation.metadata?.newly_awarded_milestones ?? []}
 *   />
 */

import { useState, useEffect } from 'react';
import { MILESTONE_DEFINITIONS } from '@/utils/milestoneDefinitions';

// Storage key pattern: one flag per evaluation so reloads never re-show.
const _storageKey = (evalId: string) => `sf_milestone_toast_shown_${evalId}`;

interface MilestoneCelebrationToastProps {
  evaluationId: string | undefined;
  milestoneIds: string[];
}

export function MilestoneCelebrationToast({
  evaluationId,
  milestoneIds,
}: MilestoneCelebrationToastProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!evaluationId || milestoneIds.length === 0) return;

    // Guard: only show once per evaluation
    try {
      const key = _storageKey(evaluationId);
      if (localStorage.getItem(key) === 'true') return;
      localStorage.setItem(key, 'true');
    } catch {
      // localStorage unavailable — show anyway
    }

    setVisible(true);

    // Auto-dismiss after 6 seconds
    const timer = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(timer);
  }, [evaluationId, milestoneIds]);

  if (!visible || milestoneIds.length === 0) return null;

  // Resolve labels + icons from the static definitions list
  const awarded = milestoneIds
    .map((id) => MILESTONE_DEFINITIONS.find((m) => m.id === id))
    .filter(Boolean) as typeof MILESTONE_DEFINITIONS;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        maxWidth: '340px',
        width: 'calc(100vw - 48px)',
        backgroundColor: 'var(--color-dark-elevated-1)',
        border: '1px solid rgba(30,215,96,0.30)',
        borderRadius: '16px',
        padding: '20px 20px 16px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(30,215,96,0.08)',
        animation: 'sfSlideUp 300ms ease forwards',
        pointerEvents: 'auto',
      }}
    >
      <style>{`
        @keyframes sfSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>

      {/* Close button */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setVisible(false)}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          color: 'var(--color-text-muted)',
          lineHeight: 1,
          padding: '4px',
        }}
      >
        ×
      </button>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '14px',
        }}
      >
        <span style={{ fontSize: '24px', lineHeight: 1 }} aria-hidden>
          🎉
        </span>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '15px',
              color: 'var(--color-text-primary)',
            }}
          >
            {awarded.length === 1 ? 'New badge earned!' : `${awarded.length} new badges earned!`}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
            Keep it up — more badges await.
          </div>
        </div>
      </div>

      {/* Badge list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {awarded.map((m) => (
          <div
            key={m.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 12px',
              borderRadius: '10px',
              backgroundColor: 'rgba(30,215,96,0.06)',
              border: '1px solid rgba(30,215,96,0.15)',
            }}
          >
            <span style={{ fontSize: '22px', lineHeight: 1 }} aria-hidden>
              {m.icon_key}
            </span>
            <div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--color-accent)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                {m.label}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '1px' }}>
                {m.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
